import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Drawer,
  Box,
  Stack,
  Divider,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { Menu, X, ChevronRight, Star, Flag, MessageSquare, LogIn as LogInIcon, LayoutDashboard, LogIn } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "utils/constants";
import { useAuth } from "hooks/useAuth";
import logo from "../../../assets/images/TC_Logo1.png";
import logo2 from "../../../assets/images/TC_Logo.png";


export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    if (window.location.pathname !== ROUTES.ROOT) {
      navigate(ROUTES.ROOT);
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 150);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };
const navLinks = [
  {
    label: "About",
    action: () => scrollToSection("about"),
    icon: <MessageSquare size={20} />,
  },
  {
    label: "Founders notes",
    action: () => scrollToSection("founders"),
    icon: <Flag size={20} />,
  },
  {
    label: "Pricing",
    action: () => navigate(ROUTES.SUBSCRIPTION_PLANS),
    icon: <Star size={20} />,
  },
];


  return (
    <>
   <AppBar
        position="fixed"
        elevation={isScrolled ? 4 : 0}
        sx={{
          backgroundColor: isScrolled ? "rgba(255,255,255,0.95)" : "transparent",
          backdropFilter: isScrolled ? "blur(6px)" : "none",
          transition: "all 0.3s ease",
          py: isScrolled ? 1 : 2,
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              gap: 1,
            }}
            onClick={() => navigate(ROUTES.ROOT)}
          >
            <Box component="img" src={logo2} alt="Logo" sx={{ width: 30, height: 30 }} />
          
         <Box sx={{ display: "flex", flexDirection: "column" }}>
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
    <Typography
      sx={{
        fontWeight: 700,
        fontSize: "1.50rem",
        background: "linear-gradient(to right, #2563EB, #6366F1)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        lineHeight: 1.1,
      }}
    >
      Theecastable
    </Typography>

    <Typography
      sx={{
        fontSize: "0.45rem",
        fontWeight: 700,
        px: "4px",
        py: "1px",
        borderRadius: "4px",
        backgroundColor: "#EEF2FF",
        color: "#4F46E5",
        lineHeight: 1,
        position: "relative",
        top: "-6px",
      }}
    >
      BETA
    </Typography>
  </Box>
</Box>


          </Box>

          <Box sx={{ display: { xs: "none", md: "flex" }, gap: 4 }}>
            {navLinks.map((link) => (
              <Button
                key={link.label}
                onClick={link.action}
                sx={{
                  color: "text.primary",
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "1.2rem",
                }}
              >
                {link.label}
              </Button>
            ))}

            {!user && (
              <Button
                variant="outlined"
                endIcon={<LogInIcon />}
                sx={{
                  textTransform: "none",
                  border: "2px solid transparent",
                  borderRadius: "10px",
                  color: "#8B5CF6",
                  px: 3,
                  background:
                    "linear-gradient() padding-box, linear-gradient(to right, #3B82F6, #38BDF8, #2DD4BF) border-box",
                  backgroundClip: "padding-box, border-box",
                }}
                onClick={() => navigate(ROUTES.LOGIN)}
              >
                Log in
              </Button>
            )}

            <Button
              variant="contained"
              endIcon={<ChevronRight size={20} />}
              sx={{
                textTransform: "none",
                borderRadius: "20px",
                px: 4,
                color: "#fff",
                background: "linear-gradient(to right, #3B82F6, #8B5CF6)",
                "&:hover": {
                  background: "linear-gradient(to right, #2563EB, #7C3AED)",
                },
              }}
              onClick={() => navigate(user ? ROUTES.APP : ROUTES.APP_ONBOARDING)}
            >
              {user ? "Go to App" : "Get Started"}
            </Button>
          </Box>

          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              alignItems: "center",
              gap: 1,
            }}
          >
            <Tooltip title={user ? "App" : "Login"} arrow>
              <Button
                variant="text"
                sx={{
                minWidth: "auto",
                p: 0.5,
                color: "#8B5CF6",
                background: "transparent",
                "&:hover": {
                 background: "transparent",
                     },
              }}
              onClick={() => navigate(user ? ROUTES.APP : ROUTES.LOGIN)}
            >
              {user ? <LayoutDashboard/> : <LogIn/>}
              </Button>
              </Tooltip>

            <IconButton onClick={() => setIsOpen(true)}>
              <Menu size={22} />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer anchor="right" open={isOpen} onClose={() => setIsOpen(false)}>
        <Box
          sx={{
            width: 240,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            pb: 2,
          }}
        >
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1,
              }}
            >
              <Stack direction="row" alignItems="center">
                <img src={logo} alt="Logo" width={24} />
                <Typography
                  sx={{ ml: 1, fontWeight: 600, cursor: "pointer" }}
                  onClick={() => navigate(ROUTES.ROOT)}
                >
                  Theecastable
                </Typography>
              </Stack>
              <IconButton onClick={() => setIsOpen(false)}>
                <X size={24} />
              </IconButton>
            </Box>

            {navLinks.map((link, i) => (
              <React.Fragment key={link.label}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => {
                      link.action();
                      setIsOpen(false);
                    }}
                  >
                    <ListItemText primary={link.label} />
                  </ListItemButton>
                </ListItem>
                {i < navLinks.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </Box>

          {/* Drawer Bottom Buttons */}
          <Box sx={{ px: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            {!user && (
              <Button
                variant="outlined"
                endIcon={<LogInIcon />}
                sx={{
                  textTransform: "none",
                  border: "2px solid #8B5CF6",
                  borderRadius: "10px",
                  color: "#8B5CF6",
                  px: 3,
                }}
                onClick={() => {
                  navigate(ROUTES.LOGIN);
                  setIsOpen(false);
                }}
              >
                Log in
              </Button>
            )}

            <Button
              variant="contained"
              endIcon={<ChevronRight size={20} />}
              sx={{
                textTransform: "none",
                borderRadius: "20px",
                px: 4,
                color: "#fff",
                background: "linear-gradient(to right, #3B82F6, #8B5CF6)",
                "&:hover": {
                  background: "linear-gradient(to right, #2563EB, #7C3AED)",
                },
              }}
              onClick={() => navigate(user ? ROUTES.APP : ROUTES.APP_ONBOARDING)}
            >
              {user ? "Go to App" : "Get Started"}
            </Button>
          </Box>
        </Box>
      </Drawer>


    </>
  );
}
